import React from 'react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Image,
    Text,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { ChevronLeft48Filled, ChevronRight48Filled } from '@fluentui/react-icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
    setCarouselIndex,
    setShowNotificationBannerDialog,
} from '../../../redux/features/notificationBanner/notificationBannerSlice';
import { NotificationBannerFeature } from '../../../domain/chat/models/NotificationBanner';
import CloseIcon from '../../../assets/quick-guide/Close.png';
import { Breakpoints, ScrollBarStyles, customColors } from '../../../styles';

const useStyles = makeStyles({
    dialog: {
        display: 'flex',
        flexDirection: 'column',
        width: '90vw',
        maxWidth: '800px',
        overflow: 'hidden',
    },
    dialogTitle: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '12px 16px 0',
        boxSizing: 'border-box',
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        marginBottom: tokens.spacingVerticalS,
    },
    title: {
        textAlign: 'center',
        color: customColors.protivitiDarkBlue,
        fontFamily: 'Inter, sans-serif',
        fontWeight: '700',
        fontSize: '25px',
        ...Breakpoints.small({ fontSize: '20px' }),
    },
    closeButton: {
        minWidth: '24px',
        height: '24px',
        padding: 0,
    },
    dialogBody: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '0 16px',
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '0',
        overflow: 'auto',
        ...ScrollBarStyles,
    },
    featureContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '10px 0',
    },
    contentContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        width: '100%',
        maxWidth: '600px',
        margin: '0 10px',
        overflow: 'hidden',
        ...Breakpoints.small({ margin: '0 5px' }),
    },
    subTitle: {
        textAlign: 'center',
        color: '#00426B80',
        fontFamily: 'Inter, sans-serif',
        fontWeight: '700',
        fontSize: '18px',
        marginBottom: '10px',
        padding: '0 5px',
        ...Breakpoints.small({ fontSize: '16px' }),
    },
    dialogImage: {
        display: 'block',
        boxShadow: '0px 4px 4px 0px #00000040',
        width: 'auto',
        maxWidth: '100%',
        height: 'auto',
        maxHeight: '200px',
        margin: '10px 0',
        objectFit: 'contain',
    },
    bannerMessage: {
        fontFamily: 'Roboto, sans-serif',
        fontWeight: '400',
        fontSize: '18px',
        color: 'rgba(45, 45, 45, 0.5)',
        lineHeight: '1.5',
        textAlign: 'center',
        width: '100%',
        maxWidth: '466px',
        marginTop: '18px',
        marginBottom: '18px',
        padding: '0 10px',
        overflow: 'auto',
        ...ScrollBarStyles,
        ...Breakpoints.medium({ fontSize: '16px', maxHeight: '100px' }),
        ...Breakpoints.small({
            fontSize: '14px',
            maxHeight: '80px',
            lineHeight: '1.4',
            marginTop: '12px',
            marginBottom: '12px',
        }),
    },
    dots: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '18px',
        marginBottom: '12px',
        gap: '10px',
        width: '100%',
    },
    dot: {
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'background 0.2s',
        ...Breakpoints.medium({ width: '14px', height: '14px' }),
        ...Breakpoints.small({ width: '12px', height: '12px' }),
    },
    navigationButton: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '44px',
        height: '44px',
        minWidth: '44px',
        minHeight: '44px',
        ...shorthands.border('2px', 'solid'),
        borderRadius: '50%',
        padding: '0',
        margin: '0 10px',
        boxSizing: 'border-box',
        ...Breakpoints.small({ width: '36px', height: '36px', minWidth: '36px', minHeight: '36px', margin: '0 5px' }),
    },
});

export const NotificationBannerDialog: React.FC = () => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const { carouselIndex, showNotificationDialog, bannerConfig } = useAppSelector((state) => state.notificationBanner);
    const features: NotificationBannerFeature[] = Array.isArray(bannerConfig?.features) ? bannerConfig.features : [];
    const currentFeature = features[carouselIndex];

    if (!features.length) {
        return null;
    }

    const handleClose = () => {
        dispatch(setShowNotificationBannerDialog(false));
    };

    const handlePrevious = () => {
        dispatch(setCarouselIndex(carouselIndex > 0 ? carouselIndex - 1 : 0));
    };

    const handleNext = () => {
        dispatch(setCarouselIndex(carouselIndex < features.length - 1 ? carouselIndex + 1 : carouselIndex));
    };

    return (
        <Dialog open={showNotificationDialog}>
            <DialogSurface className={classes.dialog}>
                <DialogTitle className={classes.dialogTitle}>
                    <div className={classes.titleContainer}>
                        <span className={classes.title}>Introducing New Features</span>
                    </div>
                    <Button
                        className={classes.closeButton}
                        icon={<Image src={CloseIcon} fit="contain" />}
                        appearance="transparent"
                        onClick={handleClose}
                    />
                </DialogTitle>
                <DialogBody className={classes.dialogBody}>
                    <DialogContent className={classes.dialogContent}>
                        {currentFeature ? (
                            <div className={classes.featureContainer}>
                                <Button
                                    icon={<ChevronLeft48Filled />}
                                    onClick={handlePrevious}
                                    disabled={carouselIndex === 0}
                                    appearance="subtle"
                                    aria-label="Previous feature"
                                    className={classes.navigationButton}
                                    style={{ borderColor: carouselIndex === 0 ? '#979797' : '#F6871F' }}
                                />

                                <div className={classes.contentContainer}>
                                    <Text className={classes.subTitle}>{currentFeature.title}</Text>
                                    {currentFeature.image ? (
                                        <Image
                                            src={currentFeature.image}
                                            fit="contain"
                                            className={classes.dialogImage}
                                        />
                                    ) : null}
                                    <Text className={classes.bannerMessage}>{currentFeature.message}</Text>
                                    <div className={classes.dots}>
                                        {features.map((_, index) => (
                                            <button
                                                key={index}
                                                className={classes.dot}
                                                aria-label={`Feature ${index + 1}`}
                                                onClick={() => dispatch(setCarouselIndex(index))}
                                                style={{
                                                    backgroundColor: index === carouselIndex ? '#D9D9D9' : '#D9D9D940',
                                                    border: 'none',
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <Button
                                    icon={<ChevronRight48Filled />}
                                    onClick={handleNext}
                                    disabled={carouselIndex === features.length - 1}
                                    appearance="subtle"
                                    aria-label="Next feature"
                                    className={classes.navigationButton}
                                    style={{
                                        borderColor: carouselIndex === features.length - 1 ? '#979797' : '#F6871F',
                                    }}
                                />
                            </div>
                        ) : null}
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default NotificationBannerDialog;
